/**
 * G7 (B1c-1) — định dạng số, tiền, ngày kiểu Việt DÙNG CHUNG cho toàn app.
 * Tiền đi dạng chuỗi thập phân như API trả ('4593055104.00'): xử lý bằng chữ số, KHÔNG đổi sang Number
 * (phần nguyên tiền (18,2) tới 16 chữ số, vượt 2^53). Múi giờ hiển thị cố định giờ Việt Nam.
 * Test canh: src/__tests__/shared/format-guard.test.ts — cấm Intl, toLocaleString và format của date-fns ngoài file này.
 */
export const EMPTY = '-'
export type NumInput = string | number | null | undefined
export type DateInput = string | Date | null | undefined

const PLAIN = /^-?\d+(\.\d+)?$/
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const TZ = 'Asia/Ho_Chi_Minh'

/** number → chuỗi thập phân thường (không số mũ); null khi NaN/Infinity */
function numberToPlain(n: number): string | null {
  if (!Number.isFinite(n)) return null
  const s = String(n)
  if (PLAIN.test(s)) return s
  return n.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 20 })
}

type Norm = { kind: 'empty' } | { kind: 'raw'; text: string } | { kind: 'plain'; text: string }

function normalize(v: NumInput): Norm {
  if (v === null || v === undefined) return { kind: 'empty' }
  if (typeof v === 'number') {
    const p = numberToPlain(v)
    return p === null ? { kind: 'empty' } : { kind: 'plain', text: p }
  }
  const t = v.trim()
  if (!t) return { kind: 'empty' }
  return PLAIN.test(t) ? { kind: 'plain', text: t } : { kind: 'raw', text: v }
}

/** Làm tròn nửa lên trên chuỗi, giữ tối đa `digits` chữ số lẻ */
function roundPlain(s: string, digits: number): string {
  const neg = s.startsWith('-')
  const body = neg ? s.slice(1) : s
  const [int, frac = ''] = body.split('.')
  if (frac.length <= digits) return s
  const kept = (int + frac.slice(0, digits)).split('').map(Number)
  if (frac.charCodeAt(digits) >= 53 /* '5' */) {
    let i = kept.length - 1
    while (i >= 0 && kept[i] === 9) kept[i--] = 0
    if (i < 0) kept.unshift(1)
    else kept[i] += 1
  }
  const all = kept.join('')
  const cut = all.length - digits
  return `${neg ? '-' : ''}${digits ? `${all.slice(0, cut)}.${all.slice(cut)}` : all}`
}

/** '1850000.50' → '1.850.000,5'; '-0.00' → '0' */
function group(plain: string): string {
  const neg = plain.startsWith('-')
  const [int, frac = ''] = (neg ? plain.slice(1) : plain).split('.')
  const i = int.replace(/^0+(?=\d)/, '')
  const f = frac.replace(/0+$/, '')
  const zero = /^0+$/.test(i) && !f
  return `${neg && !zero ? '-' : ''}${i.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}${f ? `,${f}` : ''}`
}

export function formatNumber(v: NumInput, opts: { maxFraction?: number } = {}): string {
  const n = normalize(v)
  if (n.kind === 'empty') return EMPTY
  if (n.kind === 'raw') return n.text
  return group(opts.maxFraction === undefined ? n.text : roundPlain(n.text, opts.maxFraction))
}

export function formatMoney(v: NumInput, opts: { unit?: boolean } = {}): string {
  const s = formatNumber(v, { maxFraction: 2 })
  return opts.unit && normalize(v).kind === 'plain' ? `${s} đ` : s
}

export const formatQuantity = (v: NumInput): string => formatNumber(v, { maxFraction: 4 })

const BYTE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB']

/** Plan 4 (admin): dung lượng CSDL / tệp, vd "7,6 MB"; null → "-" */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes == null || !Number.isFinite(bytes)) return EMPTY
  let v = bytes
  let i = 0
  while (v >= 1024 && i < BYTE_UNITS.length - 1) {
    v /= 1024
    i++
  }
  return `${formatNumber(String(v), { maxFraction: i === 0 ? 0 : 1 })} ${BYTE_UNITS[i]}`
}

/** Tỷ lệ → phần trăm: 0.125 → '12,5%' */
export function formatPercent(v: NumInput, digits = 1): string {
  const n = normalize(v)
  if (n.kind === 'empty') return EMPTY
  if (n.kind === 'raw') return n.text
  const neg = n.text.startsWith('-')
  const [int, frac = ''] = (neg ? n.text.slice(1) : n.text).split('.')
  const f = `${frac}00`
  const times100 = `${neg ? '-' : ''}${int}${f.slice(0, 2)}${f.length > 2 && f.slice(2) ? `.${f.slice(2)}` : ''}`
  return `${group(roundPlain(times100, digits))}%`
}

export function isNegativeNumber(v: NumInput): boolean {
  const n = normalize(v)
  return n.kind === 'plain' && group(n.text).startsWith('-')
}

// ── ngày giờ ──
const PARTS = new Intl.DateTimeFormat('en-GB', {
  timeZone: TZ,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})
const LONG = new Intl.DateTimeFormat('vi-VN', {
  timeZone: TZ,
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

interface VnParts {
  y: string
  m: string
  d: string
  H: string
  M: string
  S: string
}

function toDate(v: string | Date): Date | null {
  const d = v instanceof Date ? v : new Date(v)
  return Number.isNaN(d.getTime()) ? null : d
}

function vnParts(v: string | Date): VnParts | null {
  const date = toDate(v)
  if (!date) return null
  const p: Record<string, string> = {}
  for (const x of PARTS.formatToParts(date)) p[x.type] = x.value
  return { y: p.year, m: p.month, d: p.day, H: p.hour, M: p.minute, S: p.second }
}

const isEmpty = (v: DateInput): v is null | undefined | '' =>
  v === null || v === undefined || v === ''

/** BT1: hôm nay theo giờ Việt Nam, dạng 'YYYY-MM-DD' (mặc định kỳ báo cáo) */
export function todayIsoVn(now: Date = new Date()): string {
  const p = vnParts(now)!
  return `${p.y}-${p.m}-${p.d}`
}

export function formatDate(v: DateInput): string {
  if (isEmpty(v)) return EMPTY
  if (typeof v === 'string') {
    const m = DATE_ONLY.exec(v)
    if (m) return `${m[3]}/${m[2]}/${m[1]}`
  }
  const p = vnParts(v)
  return p ? `${p.d}/${p.m}/${p.y}` : String(v)
}

export function formatDayMonth(v: DateInput): string {
  if (isEmpty(v)) return EMPTY
  if (typeof v === 'string') {
    const m = DATE_ONLY.exec(v)
    if (m) return `${m[3]}/${m[2]}`
  }
  const p = vnParts(v)
  return p ? `${p.d}/${p.m}` : String(v)
}

export function formatTime(v: DateInput, opts: { seconds?: boolean } = {}): string {
  if (isEmpty(v)) return EMPTY
  const p = vnParts(v)
  if (!p) return String(v)
  return opts.seconds ? `${p.H}:${p.M}:${p.S}` : `${p.H}:${p.M}`
}

export function formatDateTime(
  v: DateInput,
  opts: { seconds?: boolean; timeFirst?: boolean } = {},
): string {
  if (isEmpty(v)) return EMPTY
  if (typeof v === 'string' && DATE_ONLY.test(v)) return formatDate(v)
  const p = vnParts(v)
  if (!p) return String(v)
  const date = `${p.d}/${p.m}/${p.y}`
  const time = opts.seconds ? `${p.H}:${p.M}:${p.S}` : `${p.H}:${p.M}`
  return opts.timeFirst ? `${time} ${date}` : `${date} ${time}`
}

/** 'Thứ Ba, 29 tháng 9, 2026' — lời chào đầu trang */
export function formatDateLong(v: DateInput): string {
  if (isEmpty(v)) return EMPTY
  const d = typeof v === 'string' && DATE_ONLY.test(v) ? new Date(`${v}T12:00:00+07:00`) : toDate(v)
  if (!d) return String(v)
  const s = LONG.format(d)
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ── đọc chữ người gõ (form) ──

/** Giống parseViNumber phía server: '1.850.000,5' | '1.850.000' | '12,75' | '1850000.5' → '1850000.5' */
export function parseNumberVi(text: string): string | null {
  const t = text.replace(/\s/g, '')
  if (!t) return null
  let n = t
  if (t.includes(',')) {
    if ((t.match(/,/g) ?? []).length > 1) return null
    n = t.replace(/\./g, '').replace(',', '.')
  } else if (/^-?\d{1,3}(\.\d{3})+$/.test(t)) {
    n = t.replace(/\./g, '')
  }
  return PLAIN.test(n) ? n : null
}

/** 'd/m/yyyy' → 'YYYY-MM-DD'; ngày không có trên lịch → null */
export function parseDateVi(text: string): string | null {
  const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(text.trim())
  if (!m) return null
  const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])]
  const dt = new Date(Date.UTC(y, mo - 1, d))
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return null
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`
}

/**
 * B2e-2: so hai chuỗi thập phân dạng API ('3074000000.00', '-1.5') mà không đổi sang `number` (tiền tới 16 chữ số nguyên).
 * Trả -1 / 0 / 1. Chuỗi không hợp lệ coi như 0.
 */
export function compareDecimal(a: string, b: string): -1 | 0 | 1 {
  const parse = (s: string) => {
    const t = PLAIN.test(s.trim()) ? s.trim() : '0'
    const neg = t.startsWith('-')
    const [i, f = ''] = (neg ? t.slice(1) : t).split('.')
    const int = i.replace(/^0+/, '')
    const frac = f.replace(/0+$/, '')
    return { neg: neg && (int !== '' || frac !== ''), int, frac }
  }
  const x = parse(a)
  const y = parse(b)
  if (x.neg !== y.neg) return x.neg ? -1 : 1
  const abs = (): -1 | 0 | 1 => {
    if (x.int.length !== y.int.length) return x.int.length > y.int.length ? 1 : -1
    if (x.int !== y.int) return x.int > y.int ? 1 : -1
    const n = Math.max(x.frac.length, y.frac.length)
    const xf = x.frac.padEnd(n, '0')
    const yf = y.frac.padEnd(n, '0')
    return xf === yf ? 0 : xf > yf ? 1 : -1
  }
  const r = abs()
  return x.neg ? ((-r || 0) as -1 | 0 | 1) : r
}
